/**
 * Route prefetching utility for faster navigation.
 * Preloads JS chunks for routes when users hover over navigation links.
 */

import { routeImportMap } from 'app/routes/routeImportMap';
import { urls } from 'shared/urls';

const HOVER_PREFETCH_TIMEOUT_MS = 2000;
const HOVER_PREFETCH_FALLBACK_DELAY_MS = 50;
const BACKGROUND_PREFETCH_TIMEOUT_MS = 5000;
const BACKGROUND_PREFETCH_FALLBACK_DELAY_MS = 3000;
const BACKGROUND_PREFETCH_STAGGER_MS = 500;

// Track which routes have already been prefetched to avoid duplicate requests
const prefetchedRoutes = new Set<string>();

/**
 * Prefetch a route's JS chunk.
 * Uses requestIdleCallback for non-blocking prefetch when available.
 */
export const prefetchRoute = (path: string): void => {
  // Skip if already prefetched or no import function exists
  if (prefetchedRoutes.has(path)) {
    return;
  }

  const importFn = routeImportMap[path];

  if (!importFn) {
    return;
  }

  // Mark as prefetched immediately to prevent duplicate calls
  prefetchedRoutes.add(path);

  const doPrefetch = async () => {
    try {
      await importFn();
    } catch {
      // If prefetch fails, remove from set so it can be retried
      prefetchedRoutes.delete(path);
    }
  };

  // Use requestIdleCallback for non-blocking prefetch
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(doPrefetch, { timeout: HOVER_PREFETCH_TIMEOUT_MS });
  } else {
    // Fallback for Safari and older browsers
    setTimeout(doPrefetch, HOVER_PREFETCH_FALLBACK_DELAY_MS);
  }
};

// High-priority routes to prefetch in the background after initial load
const HIGH_PRIORITY_ROUTES = [urls.orders.url(), urls.allUsers.url(), urls.reporting.url()];

/**
 * Start background prefetching of high-priority routes.
 * Call this after the initial page has loaded.
 */
export const startBackgroundPrefetch = (): void => {
  const prefetchWithDelay = (route: string, delay: number) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => prefetchRoute(route), {
        timeout: BACKGROUND_PREFETCH_TIMEOUT_MS + delay,
      });
    } else {
      setTimeout(() => prefetchRoute(route), BACKGROUND_PREFETCH_FALLBACK_DELAY_MS + delay);
    }
  };

  // Stagger prefetches to avoid overwhelming the browser
  HIGH_PRIORITY_ROUTES.forEach((route, index) => {
    prefetchWithDelay(route, index * BACKGROUND_PREFETCH_STAGGER_MS);
  });
};
